import {
  TransparencySettingsContext,
  useTransparencySettingsContext,
} from "@/components/common/transparency-sliders/transparency-settings-context";
import {
  assert,
  clamp,
  ComposeFramebuffersPass as LotvComposeFramebuffersPass,
} from "@faro-lotv/lotv";
import { RefObject, useCallback, useMemo } from "react";
import { FlyWalkRenderingAnimation } from "../data-dispatch/flywalk-animation";
import {
  FlyWalkRenderingState,
  renderingFlagByIdx,
} from "../data-dispatch/flywalk-rendering-state";

/**
 * Provides logic to implement cross-fading between subscenes of the rendering pipeline
 *
 * @param renderingState The current configuration of the rendering pipeline
 * @param animation The animation that is currently being run
 * @param composerRef A reference to the composer pass of the rendering pipeline
 * @param endAnimation Callback to be issued when the animation is over
 * @param zoomLensOn Whether the zoom lens is active, which makes the splats a composed subscene
 * @returns A callback to be called at each frame, to implement cross-fade animation
 */
export function useAnimateOpacity(
  renderingState: FlyWalkRenderingState,
  animation: FlyWalkRenderingAnimation | null,
  composerRef: RefObject<LotvComposeFramebuffersPass | null>,
  endAnimation: () => void,
  zoomLensOn: boolean,
): AnimateOpacityCbk {
  const opacityValues = useMemo(
    () => (animation ? new OpacityValues(animation) : null),
    [animation],
  );

  const transparency = useTransparencySettingsContext();

  const connector = useMemo(
    () => new OpacityConnector(renderingState, transparency, zoomLensOn),
    [renderingState, transparency, zoomLensOn],
  );

  return useCallback(
    (delta: number) => {
      if (!composerRef.current) return;
      // The connector object provides always at least three accessors.
      // If it provides more than three, an extra subscene is enabled in the composer
      // and will not be rendered anyway because of the limit of ComposeFramebuffersPass.
      // Therefore it is correct to take the first three accessors
      assert(
        connector.accessors.length >= 3,
        "OpacityConnector should always provide three accessors",
      );
      const opacity1 = connector.accessors[0]();
      const opacity2 = connector.accessors[1]();
      const opacity3 = connector.accessors[2]();
      if (!animation) {
        composerRef.current.opacity1 = opacity1;
        composerRef.current.opacity2 = opacity2;
        composerRef.current.opacity3 = opacity3;
        return;
      }
      if (!opacityValues || opacityValues.finished) return;
      for (let scene = 0; scene < opacityValues.scenes.length; scene++) {
        opacityValues.scenes[scene] += delta * opacityValues.sceneCoeffs[scene];
        const opacity = clamp(
          opacityValues.scenes[scene] / opacityValues.duration,
          0,
          1,
        );
        switch (scene) {
          case 0:
            composerRef.current.opacity1 = opacity * opacity1;
            break;
          case 1:
            composerRef.current.opacity2 = opacity * opacity2;
            break;
          case 2:
            composerRef.current.opacity3 = opacity * opacity3;
            break;
        }
      }
      opacityValues.elapsedTime += delta;
      if (opacityValues.elapsedTime > opacityValues.duration) {
        opacityValues.finished = true;
        endAnimation();
      }
    },
    [animation, composerRef, opacityValues, endAnimation, connector],
  );
}

const MAX_COMPOSABLE_SCENES = 3;
const TOTAL_SCENES_IN_PIPELINE = 6;

type AnimateOpacityCbk = (delta: number) => void;

/**
 * This class stores values and coefficients needed to cross-fade a maximum of three scenes at the same time.
 * Each of these three scene can fade in, fade out, or remain constant in opacity, during the whole animation.
 * This allows for a combinatorial amount of possible cross-fade transitions. E.g. scene0 fades in, 1 fades out, 2 invisible,
 * scene0 always visible, 1 fades in, 2 fades out, etc.
 */
class OpacityValues {
  scenes = new Array<number>();
  sceneCoeffs = new Array<number>();
  elapsedTime = 0;
  finished = false;
  duration = 0;

  constructor(animation: FlyWalkRenderingAnimation) {
    this.duration = animation.duration;
    let scene = 0;
    for (let flag = 0; flag < TOTAL_SCENES_IN_PIPELINE; flag++) {
      const startFlag = renderingFlagByIdx(animation.start, flag);
      const endFlag = renderingFlagByIdx(animation.end, flag);
      if (scene < MAX_COMPOSABLE_SCENES && (startFlag || endFlag)) {
        // storing the initial accumulation value of the opacity animation for each scene.
        // Zero if the scene is fading in, 'duration' if the scene will fade out for 'duration' seconds.
        this.scenes.push(startFlag ? this.duration : 0);
        // storing +1, -1, or 0 according to whether the scene is fading in, fading out, or not changing opacity
        this.sceneCoeffs.push(this.#computeCoeff(startFlag, endFlag));
        scene++;
      }
    }
  }

  #computeCoeff(a: boolean, b: boolean): number {
    if (a === b) return 0;
    return a ? -1 : 1;
  }
}

type Accessor = () => number;

function alwaysOne(): number {
  return 1;
}

/**
 * This class connects two different interfaces: the 'ObjectsOpacity' of the TransparencySettingsContext,
 * and the 'opacity1, opacity2, opacity3' props of 'ComposeFramebuffersPass'.
 *
 * The issue is that the ComposeFramebuffersPass can compose at the same time a maximum of three subscenes.
 * However, these three subscenes may be any subset of the six subscenes of the flywalk rendering pipeline.
 * Therefore, this class ensures that the 'opacity1, opacity2, opacity3' props are always connected to the correct
 * transparency settings stored in the context.
 */
class OpacityConnector {
  accessors: Accessor[] = [];

  constructor(
    renderingState: FlyWalkRenderingState,
    private transparencySettings: TransparencySettingsContext,
    zoomLensOn: boolean,
  ) {
    if (renderingState.floorPlanPassEnabled) {
      this.accessors.push(
        () => this.transparencySettings.objectsOpacity.mapOpacity,
      );
    }
    if (renderingState.previousImagePassEnabled) {
      this.accessors.push(alwaysOne);
    }
    if (renderingState.panoPassEnabled) {
      this.accessors.push(alwaysOne);
    }
    // Splats are only composed by the pass while the zoom lens is on, otherwise they are
    // rendered by a standalone pass that the composer opacities do not apply to.
    if (renderingState.gaussianSplatsPassEnabled && zoomLensOn) {
      this.accessors.push(alwaysOne);
    }
    if (renderingState.pointCloudPassEnabled) {
      this.accessors.push(
        () => this.transparencySettings.objectsOpacity.pcOpacity,
      );
    }
    if (renderingState.cadModelPassEnabled) {
      this.accessors.push(
        () => this.transparencySettings.objectsOpacity.cadOpacity,
      );
    }
    while (this.accessors.length < MAX_COMPOSABLE_SCENES) {
      this.accessors.push(alwaysOne);
    }
  }
}
